import { Alert, AlertTitle, Collapse, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { KeyphraseRow } from '../../../../../utils/keyphrases';
import { EnhancedTable, KeyphraseTableRow } from '../../../../Table';
import { callAPISync, updateCustomData } from '../../../../../utils/api';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { updateUserData } from '../../../../../store/system/systemSlice';
import { useLogto } from '@logto/react';

const sampleRow: KeyphraseRow = {
	pronoun: 'my',
	maxWordAllowance: 1,
	terms: 'girl',
	tag: 'contact',
	matchWholeWords: false,
	errorFields: {},
};

const KeyphraseHelpText: React.FC = () => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const keyphrasesHelpViewed = useAppSelector((state) => state.system.keyphrasesHelpViewed);
	const [helpOpen, setHelpOpen] = useState<boolean>(!keyphrasesHelpViewed);

	const registerHideHelp = () => {
		dispatch(updateUserData({ keyphrasesHelpViewed: true }));
		callAPISync(logto, (token) => {
			updateCustomData(token, { keyphrasesHelpViewed: true });
		});
	};

	const handleShowHide = () => {
		if (helpOpen) {
			registerHideHelp();
		}
		setHelpOpen(!helpOpen);
	};

	return (
		<Alert severity="info" sx={{ whiteSpace: 'pre-line' }}>
			<AlertTitle sx={{ fontWeight: 'bold' }} onClick={() => handleShowHide()} data-testid="keyphrases-help-alert-title">
				Click to {helpOpen ? 'Hide' : 'Show'} Help
			</AlertTitle>
			<Collapse in={helpOpen}>
				<Stack spacing={1}>
					<Typography>
						Chat-based pipelines allow for phrases to be searched for inside message threads. This is handled by Key Phrases Lists, and
						while a number are supplied by default, this page allows you to create your own lists to fit with specific use cases or
						situations
					</Typography>

					<Typography>
						Each entry, or row, in a Key Phrases List is constructed from <b>5 separate elements</b>
					</Typography>

					<Typography>
						<b>Pronoun:</b> this is the word that any search should start with. It{' '}
						<b>must be supplied if &quot;Max Word Allowance&quot; is more than 0</b>. If this is not included then a match will be made only
						against &quot;Terms&quot;
					</Typography>

					<Typography>
						<b>Max Word Allowance:</b> the <b>maximum number of words that can be between &quot;Pronoun&quot; and &quot;Terms&quot;</b> in
						order for a phrase to be matched
					</Typography>

					<Typography>
						<b>Terms:</b> this is one or more words which, <b>combined with &quot;Pronoun&quot;, will be searched for in threads</b>. This
						can be used as a simple word search if &quot;Max Word Allowance&quot; is 0 and &quot;Pronoun&quot; is left empty.
					</Typography>

					<Typography>
						<b>Tag:</b> how a match will be identified if it is made.{' '}
						<b>&quot;access&quot; and &quot;contact&quot; are the two most important tags</b>, as a thread with both of these will be
						flagged as a potential risk, however others can be added by typing them in. This may be useful for identifying other potentially
						interesting phrases which are not the primary focus
					</Typography>

					<Typography>
						<b>Match Whole Words:</b> if this is checked then &quot;Terms&quot; will only match if the phrase in the message thread
						constitutes a full word- as an example, &apos;vid&apos; would match &apos;video&apos; if this was unchecked but only
						&apos;vid&apos; if it was
					</Typography>

					<Typography>This is an example of a valid row:</Typography>

					<EnhancedTable
						tableTitle="Keyphrases Create Table Demo"
						defaultOrderBy=""
						headers={[]}
						rowData={[sampleRow]}
						rowFunction={() => (
							<KeyphraseTableRow
								key="keyphrase-row-demo"
								rowIdx={-1}
								row={sampleRow}
								updateRow={() => {}}
								tagOptions={['contact']}
								updateTagOptions={() => {}}
								disableDelete={true}
								onDelete={() => {}}
								onAdd={() => {}}
							/>
						)}
					/>

					<Typography>
						This row would match any combination of words beginning with &quot;my&quot; and ending with &quot;girl&quot; as long as there is
						only 1 word between them- for instance, &quot;my little girl&quot; would match. This would be considered an indicator that the
						suspect has &apos;contact&apos; with an at-risk individual, and should that be in a chat thread with an &apos;access&apos;
						indicator then that thread would be tagged as potentially worth investigating
					</Typography>

					<Typography>Cells which still need to be filled are indicated with &quot;...&quot;</Typography>

					<Typography>
						The table also supports keyboard navigation- pressing ENTER will add a new row, whilst the arrow keys will navigate Up, Down,
						Left, or Right
					</Typography>

					<Typography>
						The table supports inserting a list of words by pasting them into the &quot;Terms&quot; column, provided the words are each
						separated by a new line. Select a box in the &quot;Terms&quot; column and either right click and select paste, or CTRL+V (CMD+V
						on a Mac).
					</Typography>
				</Stack>
			</Collapse>
		</Alert>
	);
};

export default KeyphraseHelpText;
